import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
const API_URL = process.env.API_URL;

class APIService {

    async getAnalyticsData(currentStart?: string, currentEnd?: string, prevStart?: string, prevEnd?: string) {
        const header = await this.getHeaders();

        let query = '';
        if (currentStart && currentEnd && prevStart && prevEnd) {
            query = `?current_start=${currentStart}&current_end=${currentEnd}&prev_start=${prevStart}&prev_end=${prevEnd}`;
        }

        return axios.get(`${API_URL}analytics${query}`, header).then(res => res.data);
    }

    async getHeaders() {
        const res = await fetchAuthSession();
        const config = {
            headers: {
                'Content-Type': 'application/json',
                authorizationToken: `${res.tokens.idToken}`,
            },
        };
        return config;
    }

    async getIdToken(): Promise<string> {
        const res = await fetchAuthSession();
        return `${res.tokens.idToken}`;
    }

    async addTeamMember(data: any) {
        const header = await this.getHeaders();
        return axios.post(`${API_URL}users/create`, data, header).then(res => res.data);
    }

    async getTeamMember(limit: number, lastKey?: string | null) {
        const header = await this.getHeaders();

        let query = `?limit=${limit}&minimal=false`;
        if (lastKey) query += `&last_key=${encodeURIComponent(lastKey)}`;

        const res = await axios.get(`${API_URL}users/get${query}`, header);
        return res.data;
    }

    async deleteTeamMember(email: string, rad_id?: string) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}users/delete`, { ...header, data: { email: email, rad_id: rad_id } })
            .then(res => res.data);
    }

    async uploadFile(preAssignedUrl, formData, type) {
        return axios.post(preAssignedUrl, formData);
    }
    async deleteImage(imageId) {
        const header = await this.getHeaders();
        return axios.delete(API_URL + `images/${imageId}`, header).then(res => res.data);
    }

    async getGroupPreAssignedUrl(uploadId: string, totalFiles: number, fileType: string) {
        const header = await this.getHeaders();
        const data = {
            uploadId,
            totalFiles,
            fileType,
        };
        return axios
            .post(`${API_URL}dicom-upload/generate-presigned-url`, data, header)
            .then(res => res.data);
    }

    async createUploadId(patientId: string) {
        const header = await this.getHeaders();
        const data = { patientId };
        return axios.post(`${API_URL}dicom-upload/create`, data, header).then(res => res.data);
    }
    async uploadCompleted(uploadId: string) {
        const header = await this.getHeaders();
        const data = { uploadId };
        return axios.post(`${API_URL}dicom-upload/complete`, data, header).then(res => res.data);
    }

    async getAllStudies(
        limit: number,
        startDate?: string,
        endDate?: string,
        nextToken?: string | null,
        previousToken?: string | null,
        mrn?: string,
        patientName?: string
    ) {
        const header = await this.getHeaders();

        let query = `?limit=${limit}`;
        if (startDate) query += `&start_date=${startDate}`;
        if (endDate) query += `&end_date=${endDate}`;
        if (nextToken) query += `&next_token=${encodeURIComponent(nextToken)}`;
        if (previousToken) query += `&previous_token=${encodeURIComponent(previousToken)}`;
        if (mrn?.trim()) query += `&mrn=${encodeURIComponent(mrn.trim())}`;
        if (patientName?.trim()) query += `&patientName=${encodeURIComponent(patientName.trim())}`;

        const res = await axios.get(`${API_URL}studies${query}`, header);
        return res.data;
    }

    async deleteStudy(patientId: string, studyId: string) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}studies/patient/${patientId}/study/${studyId}`, header)
            .then(res => res.data)
    }

    async savePatient(data: any) {
        const header = await this.getHeaders();
        return axios.post(`${API_URL}register-patient`, data, header).then(res => res.data);
    }

    async deletePatient(patientId: string) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}patients/${patientId}`, header)
            .then(res => res.data);
    }

    async getAllPatients(params: {
        limit?: number;
        next_token?: string;
        previous_token?: string;
        mrn?: string;
        patientName?: string;
    } = {}) {
        const header = await this.getHeaders();

        const queryParams = new URLSearchParams();

        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.next_token) queryParams.append('next_token', params.next_token);
        if (params.previous_token) queryParams.append('previous_token', params.previous_token);
        if (params.mrn?.trim()) queryParams.append('mrn', params.mrn.trim());
        if (params.patientName?.trim()) queryParams.append('search_name', params.patientName.trim());

        return axios
            .get(`${API_URL}patients?${queryParams.toString().replace(/\+/g, '%20')}`, header)
            .then(res => res.data);
    }

    async getPatientProfile(patientId, fields) {
        const header = await this.getHeaders();
        return axios
            .get(`${API_URL}patients/${patientId}?fields=${fields}`, header)
            .then(res => res.data);
    }

    async modifyPatientProfile(patientId: string, updatedFields: any) {
        const header = await this.getHeaders();
        const requestBody = {
            patientId,
            ...updatedFields,
        };

        return axios
            .patch(`${API_URL}patients/modify_patient`, requestBody, header)
            .then(res => res.data);
    }

    async saveAppointment(data: {
        patientId: string;
        appointmentDateTime: string;
        appointmentType?: string;
        tests?: Array<{ testId: string; testName: string }>;
        data?: any[];
        note?: string;
        testType?: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .post(`${API_URL}appointment/patient_appointment`, data, header)
            .then(res => res.data);
    }

    async getAppointment(queryStringParams: any = {}) {
        const header = await this.getHeaders();

        const queryParams = new URLSearchParams(queryStringParams).toString();
        return axios
            .get(`${API_URL}appointment/get_appointment?${queryParams}`, header)
            .then(res => res.data);
    }

    async deleteAppointment(data) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}appointment/delete_appointment`, {
                headers: header.headers,
                data: {
                    patientId: data.PatientID,
                    appointmentDateTime: data.AppointmentDateTime,
                },
            })
            .then(res => res.data);
    }

    async modifyAppointment(data: {
        patientId: string;
        oldAppointmentDateTime: string;
        newAppointmentDateTime: string;
        tests?: Array<{ testId: string; testName: string }>;
        data?: any[];
        testType?: string;
        note?: string;
    }) {
        const header = await this.getHeaders();
        const requestBody: Record<string, any> = {
            patientId: data.patientId,
            oldAppointmentDateTime: data.oldAppointmentDateTime,
            newAppointmentDateTime: data.newAppointmentDateTime,
            data: data.data,
        };
        if (data.tests !== undefined) requestBody.tests = data.tests;
        if (data.testType !== undefined) requestBody.testType = data.testType;
        if (data.note !== undefined) requestBody.note = data.note;

        return axios
            .patch(`${API_URL}appointment/modify_appointment`, requestBody, header)
            .then(res => res.data);
    }

    async getVoiceDictationPreAssignedUrl(studyId) {
        const header = await this.getHeaders();
        const data = {
            studyId,
            extension: 'wav',
            contentType: 'audio/wav',
        };

        return axios
            .post(`${API_URL}voice-dictation/upload`, data, header)
            .then(res => res.data);
    }

    async getVoiceDictation() {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}voice-dictation/get`, header).then(res => res.data);
    }

    async deleteVoiceDictation(data) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}voice-dictation/delete`, {
                ...header,
                data: data,
            })
            .then(res => res.data);
    }

    async shareStudy(studyId: string, recipientEmail: string) {
        const url = `${API_URL}studies/share`;
        const data = {
            studyId: studyId,
            recipientEmail: recipientEmail,
        };

        const header = await this.getHeaders();
        const response = await axios.post(url, data, header);

        return response.data;
    }

    async getSharedStudy(shareId: string) {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}studies/shared?share_id=${shareId}`, header).then(res => res.data);
    }

    async getReport(studyId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${API_URL}report/get-report?studyId=${studyId}`, header)
            .then(res => res.data);
    }

    async generatePatientReport(payload, opts?) {
        const header = await this.getHeaders();
        const url = `${API_URL}report/generate-report`;

        const config: any = { ...header };
        if (opts?.signal) config.signal = opts.signal;
        if (opts?.asBlob) config.responseType = 'blob';

        const res = await axios.post(url, payload, config);
        return res.data;
    }

    async getAllReportTemplates() {
        const header = await this.getHeaders();

        return axios.get(`${API_URL}report/get-all-templates`, header).then(res => res.data);
    }

    async retrieveTemplateReport(
        data: {
            studyId: string;
            search?: string
        }) {
        const header = await this.getHeaders();
        return axios
            .post(`${API_URL}report/retrieve-template-report`, data, header)
            .then(res => res.data);
    }

    async getPreAssignedUrl(studyId) {
        const header = await this.getHeaders();
        const data = {
            studyId,
            extension: '.html',
            contentType: 'text/html',
        };
        return axios.post(`${API_URL}report/presigned-url`, data, header).then(res => res.data);
    }

    async uploadReport(preSignedUrl, formData) {
        const header = await this.getHeaders();
        return axios.post(preSignedUrl, formData, {
            headers: {
                ...header.headers,
                'Content-Type': formData.get('Content-Type'),
            },
        });
    }

    async getIcdCodesFromText(text: string) {
        const header = await this.getHeaders();
        const payload = { text };

        return axios
            .post(`${API_URL}report/icd10-codes`, payload, header)
            .then(res => res.data);
    }

    async createNote(patientId, noteText) {
        const header = await this.getHeaders();
        return axios
            .post(`${API_URL}notes/create_note`, { patientId, note: noteText }, header)
            .then(res => res.data);
    }

    async getNotes(patientId) {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}notes/${patientId}`, header).then(res => res.data);
    }

    async editNote(patientId, noteId, noteText) {
        const header = await this.getHeaders();
        return axios
            .put(`${API_URL}notes/edit_note`, { patientId, noteId, noteText }, header)
            .then(res => res.data);
    }

    async deleteNote(patientId, noteId) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}notes/delete_note`, { ...header, data: { patientId, noteId } })
            .then(res => res.data);
    }

    async createSupportTicket(subject, description) {
        const header = await this.getHeaders();
        const data = {
            subject: subject,
            description: description,
        };

        return axios.post(`${API_URL}support/ticket`, data, header).then(res => res.data);
    }

    async getDiagnosisPreAssignedUrl(studyId) {
        const header = await this.getHeaders();
        const data = {
            studyId,
            extension: 'json',
            contentType: 'application/json',
        };
        return axios
            .post(`${API_URL}diagnostic/generate-presigned-url`, data, header)
            .then(res => res.data);
    }

    async getDiagnosis(studyId) {
        const header = await this.getHeaders();
        return axios
            .get(`${API_URL}diagnostic/get-diagnostic?study_id=${studyId}`, header)
            .then(res => res.data);
    }

    async deleteDiagnostic(studyId: string, uid?: string) {
        const header = await this.getHeaders();

        let url = `${API_URL}diagnostic/delete-diagnostic?study_id=${studyId}`;
        if (uid) {
            url += `&uid=${uid}`;
        }
        return axios.delete(url, header).then(res => res.data);
    }

    async getMedicalDocPresignedUrl(patientId: any, extension: string, contentType: string) {
        const header = await this.getHeaders();
        const data = {
            patientId,
            extension,
            contentType,
        };
        return axios
            .post(`${API_URL}medical-docs/presigned-url-upload`, data, header)
            .then(res => res.data);
    }

    async uploadMedicalDoc(preSignedUrl, file) {
        return axios.post(preSignedUrl, file).then(res => res.data);
    }

    async getMedicalDocs(patientId: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${API_URL}medical-docs/get-docs`, {
                ...header,
                params: { patientId },
            })
            .then(res => res.data);
    }

    async getMinimalUsers() {
        const header = await this.getHeaders();
        const res = await axios.get(`${API_URL}users/get?minimal=true`, header);
        return res.data;
    }

    async getMe(): Promise<{ user_id: string; role: string }> {
        const header = await this.getHeaders();
        const res = await axios.get(`${API_URL}me`, header);
        return res.data;
    }

    async createWorklistTask(data: {
        study_id: string;
        rad_id: string;
        status: string;
        study_priority: string;
    }) {
        const header = await this.getHeaders();
        return axios
            .patch(`${API_URL}worklist`, data, header)
            .then(res => res.data);
    }

    async getWorklist(studyId?: string, limit?: number | null, last_key?: string | null) {
        const header = await this.getHeaders();
        const url = studyId
            ? `${API_URL}worklist?study_id=${encodeURIComponent(studyId)}&limit=${limit}${last_key ? `&last_key=${encodeURIComponent(last_key)}` : ''}`
            : `${API_URL}worklist?limit=${limit}${last_key ? `&last_key=${encodeURIComponent(last_key)}` : ''}`;
        return axios.get(url, header).then(res => res.data);
    }

    async deleteWorklistTask(study_id: string, rad_id: string) {
        const header = await this.getHeaders();
        const payload = { study_id, rad_id };
        return axios.delete(`${API_URL}worklist`, {
            ...header,
            data: payload,
        }).then(res => res.data);
    }

    async updateWorklistTask(data: {
        study_id: string;
        current_rad_id: string;
        new_rad_id: string;
        status: string;
        study_priority?: string;
    }) {
        const header = await this.getHeaders();

        return axios.patch(`${API_URL}worklist`, data, header).then(res => res.data);
    }

    async getStats(radiologistId?: string) {
        const header = await this.getHeaders();
        const url = radiologistId
            ? `${API_URL}stats?radiologistId=${radiologistId}`
            : `${API_URL}stats`;
        return axios.get(url, header).then(res => res.data);
    }

    async getBillingCost() {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}cost`, header)
            .then(res => res.data);
    }

    async getBillingUsage() {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}usage`, header)
            .then(res => res.data);
    }

    async getAllPermissions() {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}permissions`, header).then(res => res.data);
    }

    async getAllRoles() {
        const header = await this.getHeaders();
        return axios.get(`${API_URL}roles`, header).then(res => res.data);
    }

    async createRole(data: { name: string; permissions: string[] }) {
        const header = await this.getHeaders();
        return axios.post(`${API_URL}roles`, data, header).then(res => res.data);
    }

    async updateRolePermissions(roleName: string, permissions: string[]) {
        const header = await this.getHeaders();
        return axios
            .put(`${API_URL}roles/${encodeURIComponent(roleName)}`, { permissions }, header)
            .then(res => res.data);
    }

    async assignRoleToUser(email: string, role: string) {
        const header = await this.getHeaders();
        return axios
            .put(`${API_URL}users/${email}/role`, { role }, header)
            .then(res => res.data);
    }

    async getRolePermissions(roleName: string) {
        const header = await this.getHeaders();
        return axios
            .get(`${API_URL}roles/${encodeURIComponent(roleName)}`, header)
            .then(res => res.data);
    }

    async deleteRole(roleName: string) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}roles/${encodeURIComponent(roleName)}`, header)
            .then(res => res.data);
    }

    async removeRoleFromUser(email: string) {
        const header = await this.getHeaders();
        return axios
            .delete(`${API_URL}users/${email}/role`, header)
            .then(res => res.data);
    }

}

const ApiServices = new APIService();
export default ApiServices;
